import { useCpiInventorySummary } from '@gozoki/api'
import {
  CPI_POC_MAX_DAYS,
  type CpiDiscrepancies,
  cpiPocPeriodSpanDays,
  cpiPocXlsxFileName,
  isCpiPocPeriodValid,
} from '@gozoki/api-types'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { useDownloadFileMutation } from '~/hooks/use-download-file-mutation'
import { api, tuyauClient } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import { formatDateOnly, formatDateToLocalISO } from '~/utils/types/formatDates'

export const Route = createFileRoute('/_authenticated/cpi-inventory')({
  staticData: {
    permission: ['read', 'network.network.network-dashboard'],
  },
  component: CpiInventoryPage,
})
withRoutePermissions(Route)

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'
const fmtRate = (rate: number | null) => (rate === null ? '—' : `${Math.round(rate * 100)} %`)

const TONE_COLOR = {
  ok: 'text-emerald-600',
  warn: 'text-amber-600',
  bad: 'text-red-600',
} as const

// Légende sous les compteurs de sorties : borne = fin de la dernière fenêtre de remplissage.
function SortiesCaption({
  activeUntil,
  detectionActive,
}: {
  activeUntil: string | null
  detectionActive: boolean
}) {
  if (activeUntil === null) {
    return (
      <p className="mt-2 text-xs text-gray-500">
        Aucun remplissage détecté — sorties non comptées (0).
      </p>
    )
  }
  if (detectionActive) {
    return (
      <p className={`mt-2 text-xs ${TONE_COLOR.warn}`}>
        🔄 Remplissage en cours — le comptage des sorties démarre à la fermeture de la fenêtre (
        {fmtDate(activeUntil)}). Ces chiffres vont évoluer.
      </p>
    )
  }
  return (
    <p className="mt-2 text-xs text-gray-500">
      Sorties depuis le dernier remplissage ({fmtDate(activeUntil)}).
    </p>
  )
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone?: 'ok' | 'warn' | 'bad'
}) {
  const color = tone ? TONE_COLOR[tone] : 'text-gray-900'
  return (
    <div className="flex flex-col rounded-lg border border-gray-200 bg-white px-4 py-3">
      <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
      <span className={`text-2xl font-semibold ${color}`}>{value}</span>
    </div>
  )
}

// Liste guidée ADV : une ligne par référence en écart. Les périmés se sortent au gun (zone
// POUBELLE) ; passé le délai, un périmé non sorti devient un vol. Lecture seule.
function DiscrepanciesPanel({ storeId }: { storeId: number }) {
  const { data, isLoading, isError } = useQuery(
    api.stores.getCpiDiscrepancies.queryOptions(
      { params: { id: storeId } },
      // Le poller tourne toutes les 5 min : une minute suffit pour suivre une matinée ADV.
      { refetchInterval: 60_000 },
    ),
  )
  const discrepancies = data as CpiDiscrepancies | undefined
  if (isLoading) return <p className="text-sm text-gray-400">Chargement des écarts…</p>
  if (isError || !discrepancies)
    return <p className="text-sm text-red-600">Écarts indisponibles.</p>
  const count = discrepancies.lines.length
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
        À vérifier par l'ADV ({count} référence{count > 1 ? 's' : ''})
        {discrepancies.restockEvent && (
          <span className="ml-2 font-normal normal-case text-gray-500">
            — livraison du {formatDateOnly(discrepancies.restockEvent.deliveryDate)}
            {discrepancies.restockEvent.closedAt === null ? ' (remplissage en cours)' : ''}
          </span>
        )}
      </h3>
      {count === 0 ? (
        <p className="text-sm text-gray-400">Aucun écart.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Produit</TableHead>
                <TableHead>Attendu</TableHead>
                <TableHead>Lu RFID</TableHead>
                <TableHead>Reçu gun</TableHead>
                <TableHead>En stock</TableHead>
                <TableHead>Périmés à sortir</TableHead>
                <TableHead>Vols (7 j)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {discrepancies.lines.map((l) => (
                <TableRow key={l.productReference}>
                  <TableCell>
                    {l.productLabel ?? l.productReference}
                    <span className="ml-1 font-mono text-xs text-gray-400">
                      {l.productReference}
                    </span>
                  </TableCell>
                  <TableCell>{l.expected}</TableCell>
                  <TableCell className={l.read < l.expected ? TONE_COLOR.warn : ''}>
                    {l.read}
                  </TableCell>
                  <TableCell>{l.received}</TableCell>
                  <TableCell>{l.inStock}</TableCell>
                  <TableCell className={l.expiredToDispose > 0 ? TONE_COLOR.warn : ''}>
                    {l.expiredToDispose}
                  </TableCell>
                  <TableCell className={l.theftsConfirmed > 0 ? TONE_COLOR.bad : ''}>
                    {l.theftsConfirmed}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

// Bilan POC (brief Baptiste 2026-10-29) : XLSX Synthèse / Vols / Livraisons sur une période.
function PocExport({ storeId, storeLabel }: { storeId: number; storeLabel: string }) {
  const [from, setFrom] = useState(() => formatDateToLocalISO(new Date()))
  const [to, setTo] = useState(() => formatDateToLocalISO(new Date()))
  const { mutate, isPending } = useDownloadFileMutation({
    mutationFn: async () => ({
      // Le registre type la réponse d'après le contrôleur (`response.send`, void) : avec
      // `responseType: 'blob'`, Tuyau rend le fichier tel quel.
      blob: (await tuyauClient.request('stores.export_cpi_poc_xlsx', {
        params: { id: storeId },
        query: { from, to },
        responseType: 'blob',
      })) as unknown as Blob,
      // Tuyau en `responseType: 'blob'` ne rend que le corps : nom reconstruit par la règle partagée.
      filename: `${cpiPocXlsxFileName(storeLabel, from, to)}.xlsx`,
    }),
  })
  // Mêmes bornes que le serveur (422 sinon) : ordre des dates et plafond de la période.
  const invalid = !isCpiPocPeriodValid(from, to)
  const tooLong = cpiPocPeriodSpanDays(from, to) > CPI_POC_MAX_DAYS
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
      <span className="font-medium text-gray-700">Bilan POC</span>
      <label className="flex flex-col gap-1 text-xs text-gray-500">
        Du
        <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
      </label>
      <label className="flex flex-col gap-1 text-xs text-gray-500">
        Au
        <Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
      </label>
      <Button type="button" size="sm" disabled={isPending || invalid} onClick={() => mutate()}>
        {isPending ? 'Génération…' : 'Télécharger le XLSX'}
      </Button>
      {tooLong && (
        <span className={`text-xs ${TONE_COLOR.warn}`}>
          Période limitée à {CPI_POC_MAX_DAYS} jours.
        </span>
      )}
    </div>
  )
}

function CpiInventoryPage() {
  const { data, isLoading, isError } = useCpiInventorySummary()
  const [selectedStoreId, setSelectedStoreId] = useState<number | 'all'>('all')

  if (isLoading) return <div className="p-6 text-gray-500">Chargement…</div>
  if (isError) return <div className="p-6 text-red-600">Erreur de chargement.</div>
  if (!data || data.length === 0)
    return (
      <div className="p-6 text-gray-500">
        Aucune boutique avec l'inventaire RFID CPI activé (<code>cpi_inventory_enabled</code>).
      </div>
    )

  const visibleData =
    selectedStoreId === 'all' ? data : data.filter((s) => s.store.id === selectedStoreId)

  return (
    <div className="space-y-8 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Inventaire — manuel vs auto (RFID CPI)
          </h1>
          <p className="text-sm text-gray-500">
            Un vol est constaté quand un périmé n'a pas été sorti au gun ; il est daté de sa
            dernière lecture RFID. Lecture seule.
          </p>
        </div>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase tracking-wide text-gray-500">Boutique</span>
          <select
            value={selectedStoreId}
            onChange={(e) =>
              setSelectedStoreId(e.target.value === 'all' ? 'all' : Number(e.target.value))
            }
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
          >
            <option value="all">Toutes les boutiques</option>
            {data.map((s) => (
              <option key={s.store.id} value={s.store.id}>
                {s.store.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {visibleData.map((s) => {
        const blindRate =
          s.manual.inStock > 0 ? Math.round((s.auto.neverSeen / s.manual.inStock) * 100) : 0
        return (
          <section key={s.store.id} className="space-y-4 rounded-xl border border-gray-200 p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-lg font-semibold text-gray-900">{s.store.label}</h2>
              <span className="text-xs text-gray-500">
                Dernier sync RFID&nbsp;: <strong>{fmtDate(s.lastSyncAt)}</strong>
              </span>
            </div>

            {s.cpiDown && (
              <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-700">
                ⚠️ CPI hors ligne (<code>cpi_down</code>) — la détection est en pause (pas de faux «
                volé »).
              </div>
            )}

            <div
              className={
                s.restock.detectionActive
                  ? 'rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm text-emerald-800'
                  : 'rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-600'
              }
            >
              <div className="font-medium">
                {s.restock.detectionActive ? (
                  <>🟢 En remplissage — détection active jusqu'à {fmtDate(s.restock.activeUntil)}</>
                ) : (
                  <>
                    ⏸️ Détection en pause (hors remplissage) — dernier restock&nbsp;:{' '}
                    {fmtDate(s.restock.lastDetectedAt)}
                  </>
                )}
              </div>
              <div className="mt-0.5 text-xs opacity-80">
                EPC lus / {s.restock.config.windowMinutes}&nbsp;min&nbsp;:{' '}
                <strong>{s.restock.recentEpcCount}</strong> · seuil restock&nbsp;:{' '}
                {s.restock.config.epcThreshold} · fenêtre&nbsp;: {s.restock.config.detectionHours}h
              </div>
              {s.restockEvents.length > 0 && (
                <ul className="mt-1 text-xs opacity-80">
                  {s.restockEvents.map((e) => (
                    <li key={e.id}>
                      {formatDateOnly(e.deliveryDate)} · attendu {e.expectedCount} · lu{' '}
                      {e.readCount} · reçu {e.receivedCount} · taux {fmtRate(e.readRate)}
                      {e.closedAt === null ? ' (en cours)' : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <PocExport storeId={s.store.id} storeLabel={s.store.label} />

            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
                  Manuel (inventaire classique)
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <Stat label="En stock" value={s.manual.inStock} />
                  <Stat label="Sortis inventaire" value={s.manual.inventoryDeleted} />
                  <Stat label="Vendus" value={s.manual.sold} />
                </div>
                <SortiesCaption
                  activeUntil={s.restock.activeUntil}
                  detectionActive={s.restock.detectionActive}
                />
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
                  Auto (présence RFID)
                </h3>
                <div className="grid grid-cols-4 gap-3">
                  <Stat label="Vus récemment" value={s.auto.seenRecently} tone="ok" />
                  <Stat label="Jamais lus" value={s.auto.neverSeen} tone="warn" />
                  <Stat label="Périmés à sortir" value={s.auto.expiredToDispose} tone="warn" />
                  <Stat label="Vols confirmés (7 j)" value={s.auto.theftsConfirmed} tone="bad" />
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500">
              Angle mort RFID (jamais lus / en stock)&nbsp;:{' '}
              <strong className={blindRate > 50 ? 'text-amber-600' : 'text-gray-700'}>
                {blindRate}%
              </strong>{' '}
              — proxy du risque de faux positifs.
            </p>

            {s.auto.legacyPresumed > 0 && (
              <p className="text-xs text-gray-500">
                Reliquat de l'ancienne détection par seuils (présumé volé / volé, plus jamais
                posés)&nbsp;: <strong>{s.auto.legacyPresumed}</strong> — ne bouge qu'à une relecture
                RFID ou au gun.
              </p>
            )}

            <DiscrepanciesPanel storeId={s.store.id} />

            <div>
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
                Vols confirmés ({s.auto.theftsConfirmed}
                {s.auto.theftsConfirmed > s.missing.length ? `, ${s.missing.length} affichés` : ''})
              </h3>
              {s.missing.length === 0 ? (
                <p className="text-sm text-gray-400">Aucun vol confirmé.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-gray-500">
                      <tr>
                        <th className="px-3 py-2 font-medium">EPC</th>
                        <th className="px-3 py-2 font-medium">Produit</th>
                        <th className="px-3 py-2 font-medium">Libellé</th>
                        <th className="px-3 py-2 font-medium">Confirmé le</th>
                        <th className="px-3 py-2 font-medium">Dernière vue</th>
                        <th className="px-3 py-2 font-medium">Perdu à</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.missing.map((a) => (
                        <tr key={a.id} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-mono text-xs">{a.epc}</td>
                          <td className="px-3 py-2">{a.productReference}</td>
                          <td className="px-3 py-2">{a.productLabel ?? '—'}</td>
                          <td className="px-3 py-2">{fmtDate(a.confirmedAt)}</td>
                          <td className="px-3 py-2">{fmtDate(a.lastSeenAt)}</td>
                          <td className="px-3 py-2">{fmtDate(a.lostAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        )
      })}
    </div>
  )
}
